import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@wondermaps/ui";
import type { SponsorTier } from "@wondermaps/db";

interface SponsoredPOIItem {
  poiId: string;
  poiName: string;
  sponsorTier: string;
  taps: number;
  engagements: number;
  avgDwell: number;
  directions: number;
}

const TIER_ORDER: SponsorTier[] = ["gold", "silver", "bronze"];

const DESC =
  "Tracks how sponsored POIs perform compared to general POIs. Shows taps, content engagement, average dwell time, and directions requested — key proof-of-value metrics for sponsor renewal conversations.";

export function SponsoredPOIs({ data }: { data: SponsoredPOIItem[] }) {
  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Sponsored POI Performance</CardTitle>
          <CardDescription>{DESC}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">No sponsored POI data in this period.</p>
        </CardContent>
      </Card>
    );
  }

  const grouped = TIER_ORDER.map((tier) => ({
    tier,
    items: data.filter((d) => d.sponsorTier === tier),
  })).filter((g) => g.items.length > 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sponsored POI Performance</CardTitle>
        <CardDescription>{DESC}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {grouped.map(({ tier, items }) => (
          <div key={tier}>
            <Badge variant="outline" className="mb-2 capitalize">
              {tier}
            </Badge>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>POI</TableHead>
                  <TableHead className="text-right">Taps</TableHead>
                  <TableHead className="text-right">Engagements</TableHead>
                  <TableHead className="text-right">Avg Dwell (s)</TableHead>
                  <TableHead className="text-right">Directions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.poiId}>
                    <TableCell className="font-medium">{item.poiName}</TableCell>
                    <TableCell className="text-right">{item.taps.toLocaleString()}</TableCell>
                    <TableCell className="text-right">{item.engagements.toLocaleString()}</TableCell>
                    <TableCell className="text-right">{item.avgDwell}s</TableCell>
                    <TableCell className="text-right">{item.directions.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
